import React, { useEffect, useState } from 'react';
import { Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import { ChannelNotice } from '../types';

interface NoticeBannerProps {
  channelId: number;
  notice: ChannelNotice;
  /** 배너 탭 → 원문 위치로 점프 */
  onJumpToMessage: (messageId: number) => void;
  /** 공지 내리기 (서버 해제). 권한 없으면 미전달 → 버튼 숨김 */
  onClearNotice?: () => void;
}

/**
 * 채팅방 상단 고정 공지 배너 (카카오톡 패턴).
 * 접어두기는 사용자별 로컬 상태(localStorage) — 키에 messageId가 포함되어
 * 새 공지가 등록되면 자동으로 다시 노출된다. 접힌 동안은 우측 상단 미니 칩으로 축소.
 */
export const NoticeBanner: React.FC<NoticeBannerProps> = ({
  channelId,
  notice,
  onJumpToMessage,
  onClearNotice,
}) => {
  const storageKey = `noticeCollapsed:${channelId}:${notice.messageId}`;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(storageKey) === '1');
  const [expanded, setExpanded] = useState(false);

  // 채널 전환/공지 교체 시 상태 재계산 (새 공지면 collapsed가 자연히 false)
  useEffect(() => {
    setCollapsed(localStorage.getItem(storageKey) === '1');
    setExpanded(false);
  }, [storageKey]);

  if (collapsed) {
    return (
      <button
        onClick={() => {
          localStorage.removeItem(storageKey);
          setCollapsed(false);
        }}
        className="absolute top-[3.75rem] md:top-[4.25rem] right-3 z-20 p-2 bg-white/95 border border-slate-200 rounded-full shadow-md text-amber-500 hover:bg-amber-50 transition-colors"
        title="공지 보기"
      >
        <Megaphone size={16} />
      </button>
    );
  }

  return (
    <div className="shrink-0 border-b border-amber-100 bg-amber-50">
      <div className="flex items-center gap-2 px-3 md:px-4 py-2">
        <Megaphone size={16} className="text-amber-500 shrink-0" />
        <button
          onClick={() => onJumpToMessage(notice.messageId)}
          className="flex-1 min-w-0 text-left text-sm text-slate-700 truncate"
          title="원문으로 이동"
        >
          {notice.text}
        </button>
        <button
          onClick={() => setExpanded(v => !v)}
          className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
          title={expanded ? '접기' : '펼치기'}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      {expanded && (
        <div className="px-3 md:px-4 pb-2">
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-anywhere max-h-40 overflow-y-auto">
            {notice.text}
          </p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-400">{notice.senderName}</span>
            <div className="flex items-center gap-1">
              {onClearNotice && (
                <button
                  onClick={onClearNotice}
                  className="px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 rounded transition-colors"
                >
                  공지 내리기
                </button>
              )}
              <button
                onClick={() => {
                  localStorage.setItem(storageKey, '1');
                  setCollapsed(true);
                  setExpanded(false);
                }}
                className="px-2 py-1 text-xs text-slate-500 hover:bg-amber-100 rounded transition-colors"
              >
                접어두기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
